import { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './components/dashboard/Header';
import { Sidebar } from './components/dashboard/Sidebar';
import { NetworkStats } from './components/network/NetworkStats';
import { NetworkVisualization } from './components/network/NetworkVisualization';
import { SpecializedNetworks } from './components/network/SpecializedNetworks';
import { CDNPanel } from './components/cdn/CDNPanel';
import { WASMModules } from './components/wasm/WASMModules';
import { MCPTools } from './components/mcp/MCPTools';
import { CreditsPanel } from './components/dashboard/CreditsPanel';
import { ConsolePanel } from './components/dashboard/ConsolePanel';
import { ActivityPanel } from './components/dashboard/ActivityPanel';
import { SettingsPanel } from './components/dashboard/SettingsPanel';
import { IdentityPanel } from './components/identity/IdentityPanel';
import { DocumentationPanel } from './components/docs/DocumentationPanel';
import { CrystalLoader } from './components/common/CrystalLoader';
import { ConsentWidget } from './components/common/ConsentWidget';
import { useNetworkStore } from './stores/networkStore';
import { creditsService } from './services/creditsService';

// Lazy load new panels
const WorkersPanel = lazy(() => import('./components/workers/WorkersPanel'));
const AgentsPanel = lazy(() => import('./components/agents/AgentsPanel'));
const PluginsPanel = lazy(() => import('./components/plugins/PluginsPanel'));
const GenesisPanel = lazy(() => import('./components/genesis/GenesisPanel'));

function App() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { initializeEdgeNet, updateRealStats, isWASMReady } = useNetworkStore();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize real EdgeNet WASM module
  useEffect(() => {
    const init = async () => {
      try {
        await initializeEdgeNet();
        console.log('[App] EdgeNet initialized, WASM ready:', isWASMReady);
      } catch (error) {
        console.error('[App] EdgeNet initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [initializeEdgeNet, isWASMReady]);

  // Update real stats from EdgeNet node
  useEffect(() => {
    const interval = setInterval(updateRealStats, 1000);
    return () => clearInterval(interval);
  }, [updateRealStats]);

  // Initialize and manage creditsService based on contribution settings
  const contributionEnabled = useNetworkStore(s => s.contributionSettings.enabled);
  const cpuLimit = useNetworkStore(s => s.contributionSettings.cpuLimit);
  const storeCredits = useNetworkStore(s => s.credits);
  const firebasePeers = useNetworkStore(s => s.firebasePeers);
  // setCredits available via store but credits are managed via addPendingContribution

  useEffect(() => {
    // Initialize creditsService with current store values
    creditsService.initialize({
      available: storeCredits.available,
      earned: storeCredits.earned,
      spent: storeCredits.spent,
      pending: storeCredits.pending,
    });
  }, []);

  useEffect(() => {
    creditsService.updateNetworkPeers(firebasePeers.length);
  }, [firebasePeers.length]);

  useEffect(() => {
    if (contributionEnabled) {
      creditsService.startEarning(cpuLimit);
      console.log('[App] CreditsService started earning at', cpuLimit, '% CPU');
    } else {
      creditsService.stopEarning();
    }
  }, [contributionEnabled, cpuLimit]);

  // Track credits earned by creditsService and add to pending contributions
  const addPendingContribution = useNetworkStore(s => s.addPendingContribution);
  const lastEarnedRef = useRef(0);

  // Sync creditsService state back to store, tracking new earnings as pending
  useEffect(() => {
    const unsubscribe = creditsService.subscribe((state) => {
      // Calculate how much was earned since last update
      const newEarnings = state.earned - lastEarnedRef.current;
      lastEarnedRef.current = state.earned;

      // If new earnings, add to pending contributions (for QDAG sync)
      if (newEarnings > 0 && contributionEnabled) {
        addPendingContribution(newEarnings);
      }
    });
    return unsubscribe;
  }, [addPendingContribution, contributionEnabled]);

  // Render active tab content
  const renderContent = () => {
    const content = {
      overview: (
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              <span className="bg-gradient-to-r from-sky-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">
                Network Overview
              </span>
            </h1>
            <p className="text-zinc-400">
              Monitor your distributed compute network in real-time
            </p>
          </motion.div>
          {/* Credits Summary */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="crystal-card p-4">
              <p className="text-sm text-zinc-400 mb-1">Credits Earned</p>
              <p className="text-2xl font-bold text-emerald-400">{storeCredits.earned.toFixed(2)}</p>
              <p className="text-xs text-zinc-500 mt-1">rUv</p>
            </div>
            <div className="crystal-card p-4">
              <p className="text-sm text-zinc-400 mb-1">Available</p>
              <p className="text-2xl font-bold text-sky-400">{storeCredits.available.toFixed(2)}</p>
              <p className="text-xs text-zinc-500 mt-1">rUv</p>
            </div>
            <div className="crystal-card p-4">
              <p className="text-sm text-zinc-400 mb-1">Peers Online</p>
              <p className="text-2xl font-bold text-violet-400">{firebasePeers.length}</p>
              <p className="text-xs text-zinc-500 mt-1">connected</p>
            </div>
            <div className="crystal-card p-4">
              <p className="text-sm text-zinc-400 mb-1">Status</p>
              <p className={`text-2xl font-bold ${contributionEnabled ? 'text-emerald-400' : 'text-amber-400'}`}>
                {contributionEnabled ? 'Active' : 'Idle'}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{contributionEnabled ? 'earning' : 'paused'}</p>
            </div>
          </motion.div>
          <NetworkStats />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NetworkVisualization />
            <motion.div
              className="crystal-card p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors text-left"
                  onClick={() => setActiveTab('credits')}
                >
                  <p className="font-medium text-white">Credits</p>
                  <p className="text-xs text-zinc-400 mt-1">Earn & spend rUv</p>
                </button>
                <button
                  className="p-4 rounded-lg bg-sky-500/10 border border-sky-500/30 hover:bg-sky-500/20 transition-colors text-left"
                  onClick={() => setActiveTab('workers')}
                >
                  <p className="font-medium text-white">Workers</p>
                  <p className="text-xs text-zinc-400 mt-1">View compute nodes</p>
                </button>
                <button
                  className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/30 hover:bg-violet-500/20 transition-colors text-left"
                  onClick={() => setActiveTab('agents')}
                >
                  <p className="font-medium text-white">AI Agents</p>
                  <p className="text-xs text-zinc-400 mt-1">Manage agents</p>
                </button>
                <button
                  className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors text-left"
                  onClick={() => setActiveTab('network')}
                >
                  <p className="font-medium text-white">Networks</p>
                  <p className="text-xs text-zinc-400 mt-1">Join communities</p>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      ),
      network: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">
            <span className="bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">
              Network & Communities
            </span>
          </h1>
          <p className="text-zinc-400">Join specialized networks to earn credits by contributing compute</p>
          <NetworkStats />
          <SpecializedNetworks />
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-zinc-300 mb-4">Network Topology</h2>
            <NetworkVisualization />
          </div>
        </div>
      ),
      wasm: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">WASM Modules</h1>
          <WASMModules />
        </div>
      ),
      cdn: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">CDN Script Manager</h1>
          <CDNPanel />
        </div>
      ),
      mcp: <MCPTools />,
      credits: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">Credit Economy</h1>
          <CreditsPanel />
        </div>
      ),
      identity: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              Identity & Networks
            </span>
          </h1>
          <p className="text-zinc-400">Manage your cryptographic identity and network participation</p>
          <IdentityPanel />
        </div>
      ),
      console: <ConsolePanel />,
      activity: <ActivityPanel />,
      settings: <SettingsPanel />,
      workers: (
        <Suspense fallback={<CrystalLoader size="lg" text="Loading Workers..." />}>
          <WorkersPanel />
        </Suspense>
      ),
      agents: (
        <Suspense fallback={<CrystalLoader size="lg" text="Loading Agents..." />}>
          <AgentsPanel />
        </Suspense>
      ),
      plugins: (
        <Suspense fallback={<CrystalLoader size="lg" text="Loading Plugins..." />}>
          <PluginsPanel />
        </Suspense>
      ),
      genesis: (
        <Suspense fallback={<CrystalLoader size="lg" text="Loading Genesis..." />}>
          <GenesisPanel />
        </Suspense>
      ),
      docs: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">
            <span className="bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">
              Documentation
            </span>
          </h1>
          <p className="text-zinc-400">Learn how to use Edge-Net and integrate it into your projects</p>
          <DocumentationPanel />
        </div>
      ),
    };

    return content[activeTab as keyof typeof content] || content.overview;
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CrystalLoader size="lg" text="Initializing Edge-Net..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        onMenuToggle={() => setIsSidebarOpen(true)}
        isMobile={isMobile}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isMobile={isMobile}
        />

        <main className="flex-1 overflow-auto p-4 md:p-6 quantum-grid">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="max-w-7xl mx-auto"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Floating consent widget for CPU/GPU contribution */}
      <ConsentWidget />
    </div>
  );
}

export default App;
